import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingCart, ArrowLeft, MinusCircle, PlusCircle } from 'lucide-react';

interface Prescription {
  id: string;
  product_name: string;
  quantity: number;
  status: string;
  repeats?: number;
  orderQuantity?: number;
}

const RenewalConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState<Prescription[]>([]);
  
  useEffect(() => {
    // Get selected prescription IDs from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const selectedIds = searchParams.get('selected')?.split(',') || [];
    
    if (selectedIds.length === 0) {
      setIsLoading(false);
      return;
    }
    
    // Fetch prescription details - direct fetch for each ID
    const fetchPrescriptions = async () => {
      try {
        // Log the selected IDs for debugging
        console.log('Selected prescription IDs:', selectedIds);
        
        // If coming from UserPrescriptions, we might have a direct ID
        if (selectedIds.length === 1) {
          // Direct fetch for a single prescription
          const response = await fetch(`/api/prescriptions/${selectedIds[0]}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch prescription ${selectedIds[0]}`);
          }
          
          const prescription = await response.json();
          // Initialize with orderQuantity of 1
          const prescriptionWithOrderQuantity = {
            ...prescription,
            orderQuantity: 1
          };
          setSelectedPrescriptions([prescriptionWithOrderQuantity]);
          console.log('Loaded single prescription:', prescriptionWithOrderQuantity);
        } else {
          // Fetch all and filter (original approach)
          const response = await fetch('/api/prescriptions', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Failed to fetch prescriptions');
          }
          
          const prescriptions = await response.json();
          
          // Filter to only include selected prescriptions
          const selected = prescriptions
            .filter((p: Prescription) => selectedIds.includes(p.id.toString()))
            .map((p: Prescription) => ({
              ...p,
              orderQuantity: 1 // Initialize with orderQuantity of 1
            }));
          
          console.log('Loaded multiple prescriptions:', selected);
          setSelectedPrescriptions(selected);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching prescriptions:', err);
        setError('Failed to load selected prescriptions');
        setIsLoading(false);
      }
    };
    
    fetchPrescriptions();
  }, [token, location.search]);
  
  // Handle back button click
  const handleGoBack = () => {
    navigate(-1); // Go back to previous page
  };
  
  // Handle quantity adjustment
  const handleQuantityChange = (id: string, increment: number) => {
    setSelectedPrescriptions(prevPrescriptions => 
      prevPrescriptions.map(prescription => {
        if (prescription.id !== id) return prescription;
        
        // Calculate new quantity
        const currentQuantity = prescription.orderQuantity || 1;
        const newQuantity = Math.max(1, Math.min(
          // Maximum of 2 or number of repeats remaining (if available)
          prescription.repeats !== undefined && prescription.repeats > 0 
            ? Math.min(2, prescription.repeats) 
            : 2,
          currentQuantity + increment
        ));
        
        return {
          ...prescription,
          orderQuantity: newQuantity
        };
      })
    );
  };
  
  // Calculate total items in cart (considering quantities)
  const getTotalItemCount = () => {
    return selectedPrescriptions.reduce((total, prescription) => 
      total + (prescription.orderQuantity || 1), 0);
  };
  
  // Handle checkout
  const handleCheckout = async () => {
    if (selectedPrescriptions.length === 0) {
      setError('No prescriptions selected');
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // Include order quantities in the request
      const response = await fetch('/api/prescriptions/renew-multiple', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          prescriptionItems: selectedPrescriptions.map(p => ({
            id: p.id,
            quantity: p.orderQuantity || 1
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process renewal');
      }

      const data = await response.json();
      
      if (data.partialOrder) {
        // Handle partial order (some products unavailable)
        let unavailableList = '';
        
        if (Array.isArray(data.unavailableProducts)) {
          // Check if the array contains objects with product_name property
          if (data.unavailableProducts.length > 0 && typeof data.unavailableProducts[0] === 'object') {
            unavailableList = data.unavailableProducts
              .map((p: any) => p.product_name || JSON.stringify(p))
              .join(', ');
          } else {
            unavailableList = data.unavailableProducts.join(', ');
          }
        } else {
          // If not an array, convert to string as fallback
          unavailableList = String(data.unavailableProducts || 'Unknown products');
        }
        
        setError(`Some products are unavailable: ${unavailableList}. We've removed them from your order.`);
        
        // After 3 seconds, redirect to Shopify checkout
        setTimeout(() => {
          window.location.href = data.shopifyUrl;
        }, 3000);
      } else if (data.shopifyUrl) {
        // All products available, redirect immediately
        window.location.href = data.shopifyUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      // Check if this is a repeats error
      if (error.response && error.response.data && error.response.data.type === 'NO_REPEATS_REMAINING') {
        // Handle no repeats remaining error
        const invalidItems = error.response.data.invalidPrescriptions || [];
        const productNames = invalidItems.map((item: any) => item.product_name).join(', ');
        setError(`Cannot renew: ${productNames} has no repeats left. Please contact your doctor for a new prescription.`);
      } else {
        setError(error.message || 'An error occurred during renewal');
      }
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto space-y-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Error</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-red-500">{error}</p>
              <Button onClick={handleGoBack}>Go back</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  if (selectedPrescriptions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>No products selected</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">You haven't selected any products to renew.</p>
              <Button onClick={handleGoBack}>Go back to select products</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="w-full">
          <CardHeader className="flex flex-row items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              className="mr-2"
              onClick={handleGoBack}
              disabled={isProcessing}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <CardTitle>Confirm Your Order</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-4">Selected Items</h3>
                <div className="space-y-3">
                  {selectedPrescriptions.map((prescription) => (
                    <div
                      key={prescription.id}
                      className="border rounded-lg p-4"
                    >
                      <p className="font-medium">{prescription.product_name}</p>
                      <div className="flex flex-col mt-2 space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Prescription Quantity: {prescription.quantity}
                        </p>
                        {prescription.repeats !== undefined && (
                          <p className="text-sm text-muted-foreground">
                            Repeats Remaining: {prescription.repeats}
                          </p>
                        )}
                        <div className="flex items-center mt-2">
                          <span className="mr-3 text-sm font-medium">Order Quantity:</span>
                          <div className="flex items-center space-x-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleQuantityChange(prescription.id, -1)}
                              disabled={
                                isProcessing || 
                                (prescription.orderQuantity || 1) <= 1
                              }
                            >
                              <MinusCircle className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center">
                              {prescription.orderQuantity || 1}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleQuantityChange(prescription.id, 1)}
                              disabled={
                                isProcessing || 
                                (prescription.orderQuantity || 1) >= 2 || 
                                (prescription.repeats !== undefined && 
                                 (prescription.orderQuantity || 1) >= prescription.repeats)
                              }
                            >
                              <PlusCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  {error}
                </div>
              )}

              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    <span className="font-semibold">
                      {getTotalItemCount()} items in cart
                    </span>
                  </div>
                </div>

                <Button 
                  className="w-full"
                  onClick={handleCheckout}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Proceed to Checkout'
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RenewalConfirmationPage;